const resources = {
  "en": {
    "goalManager": {
      "title": "My Finance Goals",
      "pageDescription": "Track goal progress, manage savings plans, and move money into goal vaults without leaving this workspace.",
      "sections": {
        "overview": {
          "title": "Goals overview",
          "description": "Review progress, inspect active plans, and act on each goal from one place."
        }
      },
      "summary": {
        "title": "Goals snapshot",
        "caption": "A quick read on how your current goal portfolio is moving.",
        "totalGoals": {
          "label": "Total goals",
          "supporting": "All goals currently in this workspace"
        },
        "activeGoals": {
          "label": "Active goals",
          "supporting": "Goals still in progress"
        },
        "activePlans": {
          "label": "Active plans",
          "supporting": "Goals with a live savings plan"
        },
        "averageProgress": {
          "label": "Average progress",
          "supporting": "Average completion across all goals",
          "supportingWithAchieved": "{{count}} already achieved"
        }
      },
      "view": {
        "auto": "Auto",
        "full": "Full",
        "compact": "Compact"
      },
      "actions": {
        "add": "Add",
        "transfer": "Transfer",
        "edit": "Edit",
        "delete": "Delete",
        "cancel": "Cancel",
        "save": "Save",
        "back": "Back"
      },
      "list": {
        "empty": "No goals yet",
        "amount": "Amount: {{amount}}",
        "targetDate": "Target Date: {{date}}"
      },
      "popconfirm": {
        "deleteTitle": "Delete this goal?",
        "ok": "Delete",
        "cancel": "Cancel"
      },
      "form": {
        "addTitle": "Add Goal",
        "editTitle": "Edit Goal",
        "fields": {
          "title": {
            "label": "Title",
            "required": "Please enter title"
          },
          "amount": {
            "label": "Amount",
            "required": "Please enter amount"
          },
          "targetDate": {
            "label": "Target Date"
          },
          "description": {
            "label": "Description"
          }
        }
      },
      "messages": {
        "loadFailed": "❌ Failed to load goals",
        "fundingAccountsLoadFailed": "❌ Failed to load funding accounts",
        "progressLoadFailed": "❌ Failed to load progress for \"{{title}}\"",
        "goalCreated": "✅ Goal created",
        "goalUpdated": "✅ Goal updated",
        "goalDeleted": "✅ Goal deleted",
        "errorWithReason": "❌ {{error}}"
      },
      "progress": {
        "contributed": "Contributed: {{value}}",
        "target": "Target: {{value}}",
        "goal": "Goal progress",
        "estimatedCompatibility": "Includes estimated compatibility-valued contributions.",
        "partialCompatibility": "Some contributions stay outside the exact goal-currency total until a native same-currency valuation is available."
      },
      "contract": {
        "title": "Contract",
        "none": "No active contract",
        "frequency": "Every {{value}}",
        "amount": "Payment: {{value}}",
        "expectedDate": "Expected end: {{date}}",
        "noDate": "N/A",
        "expectedSoFar": "Expected so far: {{value}}",
        "actualSoFar": "Paid so far: {{value}}",
        "details": "Details",
        "detailsSoon": "More details coming soon",
        "create": "Create plan",
        "calcFailed": "Failed to calculate plan",
        "createFailed": "Failed to create contract",
        "created": "Contract created",
        "autoTasks": "Auto-generate tasks",
        "autoTaskGeneration": "Auto-generate tasks",
        "preview": "Preview",
        "previewSummary": "You will save {{amount}}, total installments {{n}}, end date {{endDate}}",
        "upcoming": "Upcoming installments",
        "frequencyLabel": "Frequency",
        "frequencyOptions": {
          "daily": "Daily",
          "weekly": "Weekly",
          "twice_monthly": "Twice monthly",
          "monthly": "Monthly"
        },
        "startDate": "Start date",
        "targetDate": "Target date",
        "amountLabel": "Installment amount",
        "wizard": {
          "title": "Create plan for \"{{goal}}\"",
          "stepParams": "Parameters",
          "stepPreview": "Preview",
          "preview": "Preview",
          "create": "Create plan"
        },
        "mode": {
          "label": "Mode",
          "fixed_amount": "Fixed amount",
          "fixed_date": "Fixed date"
        },
        "status": {
          "on_track": "On track",
          "behind": "Behind"
        },
        "detailsTitle": "Contract details for \"{{goal}}\"",
        "transferModalTitle": "Transfer to \"{{goal}}\"",
        "transferLinksToPlan": "This transfer will be counted toward the active savings plan for this goal.",
        "planProgress": "Plan progress",
        "installmentsTitle": "Installments",
        "columns": {
          "dueDate": "Due date",
          "amount": "Amount",
          "status": "Status",
          "paid": "Paid"
        },
        "contributeTitle": "Transfer to this goal",
        "contributeDescription": "This creates an immediate transfer from the selected account into the Goal Vault. Only {{currency}} accounts are available here.",
        "contribute": "Transfer now",
        "selectAccount": "Source account",
        "accountPlaceholder": "Select a {{currency}} account",
        "accountRequired": "Please select a source account",
        "noEligibleAccounts": "No eligible {{currency}} account is available. Add or update an account in this currency first.",
        "amountHint": "Decimals are supported. Use a dot, for example: 12.34",
        "amountInvalid": "Enter a valid positive amount using a dot, for example: 12.34",
        "dateLabel": "Transfer date",
        "confirmNow": "Confirm now",
        "detailsFailed": "Failed to load contract details",
        "paymentRecorded": "Transfer recorded",
        "paymentRecordedDetails": "The money was transferred to this goal immediately and the progress has been updated.",
        "paymentTaskCreated": "Payment task created"
      }
    }
  },
  "uk": {
    "goalManager": {
      "title": "Мої фінансові цілі",
      "pageDescription": "Відстежуйте прогрес цілей, керуйте планами накопичення та поповнюйте goal vault з одного робочого простору.",
      "sections": {
        "overview": {
          "title": "Огляд цілей",
          "description": "Переглядайте прогрес, активні плани та дії по кожній цілі в одному місці."
        }
      },
      "summary": {
        "title": "Зведення по цілях",
        "caption": "Швидкий зріз того, як рухається ваш поточний набір фінансових цілей.",
        "totalGoals": {
          "label": "Усього цілей",
          "supporting": "Усі цілі в цьому просторі"
        },
        "activeGoals": {
          "label": "Активні цілі",
          "supporting": "Цілі, які ще в процесі"
        },
        "activePlans": {
          "label": "Активні плани",
          "supporting": "Цілі з діючим планом накопичення"
        },
        "averageProgress": {
          "label": "Середній прогрес",
          "supporting": "Середній відсоток виконання по всіх цілях",
          "supportingWithAchieved": "Уже досягнуто: {{count}}"
        }
      },
      "view": {
        "auto": "Авто",
        "full": "Широкий",
        "compact": "Компактний"
      },
      "actions": {
        "add": "Додати",
        "transfer": "Переказати",
        "edit": "Редагувати",
        "delete": "Видалити",
        "cancel": "Скасувати",
        "save": "Зберегти",
        "back": "Назад"
      },
      "list": {
        "empty": "Поки немає цілей",
        "amount": "Сума: {{amount}}",
        "targetDate": "Дата досягнення: {{date}}"
      },
      "popconfirm": {
        "deleteTitle": "Видалити цю ціль?",
        "ok": "Видалити",
        "cancel": "Скасувати"
      },
      "form": {
        "addTitle": "Додати ціль",
        "editTitle": "Редагувати ціль",
        "fields": {
          "title": {
            "label": "Назва",
            "required": "Вкажіть назву"
          },
          "amount": {
            "label": "Сума",
            "required": "Вкажіть суму"
          },
          "targetDate": {
            "label": "Кінцева дата"
          },
          "description": {
            "label": "Опис"
          }
        }
      },
      "messages": {
        "loadFailed": "❌ Не вдалося завантажити цілі",
        "fundingAccountsLoadFailed": "❌ Не вдалося завантажити рахунки для поповнення",
        "progressLoadFailed": "❌ Не вдалося завантажити прогрес для \"{{title}}\"",
        "goalCreated": "✅ Ціль створено",
        "goalUpdated": "✅ Ціль оновлено",
        "goalDeleted": "✅ Ціль видалено",
        "errorWithReason": "❌ {{error}}"
      },
      "progress": {
        "contributed": "Внесено: {{value}}",
        "target": "Мета: {{value}}",
        "goal": "Прогрес цілі",
        "estimatedCompatibility": "Містить внески, оцінені через compatibility valuation.",
        "partialCompatibility": "Частина внесків поки не входить у точний підсумок валюти цілі, доки немає native same-currency valuation."
      },
      "contract": {
        "title": "План",
        "none": "Немає активного плану",
        "frequency": "Кожні {{value}}",
        "amount": "Платіж: {{value}}",
        "expectedDate": "Очікувана дата завершення: {{date}}",
        "noDate": "Н/Д",
        "expectedSoFar": "Очікувано на зараз: {{value}}",
        "actualSoFar": "Сплачено на зараз: {{value}}",
        "details": "Деталі",
        "detailsSoon": "Скоро будуть деталі",
        "create": "Створити план",
        "calcFailed": "Не вдалося розрахувати план",
        "createFailed": "Не вдалося створити план",
        "created": "План створено",
        "autoTasks": "Автоматично створювати задачі",
        "autoTaskGeneration": "Автоматично створювати задачі",
        "preview": "Попередній перегляд",
        "previewSummary": "Ви заощадите {{amount}}, кількість платежів {{n}}, завершення {{endDate}}",
        "upcoming": "Найближчі платежі",
        "frequencyLabel": "Частота",
        "frequencyOptions": {
          "daily": "Щодня",
          "weekly": "Щотижня",
          "twice_monthly": "Двічі на місяць",
          "monthly": "Щомісяця"
        },
        "startDate": "Початкова дата",
        "targetDate": "Кінцева дата",
        "amountLabel": "Сума платежу",
        "wizard": {
          "title": "Створення плану для \"{{goal}}\"",
          "stepParams": "Параметри",
          "stepPreview": "Попередній перегляд",
          "preview": "Попередній перегляд",
          "create": "Створити план"
        },
        "mode": {
          "label": "Режим",
          "fixed_amount": "Фіксована сума",
          "fixed_date": "Фіксована дата"
        },
        "status": {
          "on_track": "За графіком",
          "behind": "Відстає"
        },
        "detailsTitle": "Деталі плану для \"{{goal}}\"",
        "transferModalTitle": "Переказ у \"{{goal}}\"",
        "transferLinksToPlan": "Цей переказ буде зарахований у активний план накопичення для цієї цілі.",
        "planProgress": "Прогрес плану",
        "installmentsTitle": "Платежі",
        "columns": {
          "dueDate": "Дата",
          "amount": "Сума",
          "status": "Статус",
          "paid": "Сплачено"
        },
        "contributeTitle": "Переказати в цю ціль",
        "contributeDescription": "Це одразу створить переказ з обраного рахунку у Goal Vault. Тут доступні лише рахунки у валюті {{currency}}.",
        "contribute": "Переказати зараз",
        "selectAccount": "Рахунок списання",
        "accountPlaceholder": "Оберіть рахунок у валюті {{currency}}",
        "accountRequired": "Оберіть рахунок списання",
        "noEligibleAccounts": "Немає доступного рахунку у валюті {{currency}}. Спочатку додайте або оновіть рахунок у цій валюті.",
        "amountHint": "Дробова частина підтримується. Використовуйте крапку, наприклад: 12.34",
        "amountInvalid": "Введіть коректну додатну суму через крапку, наприклад: 12.34",
        "dateLabel": "Дата переказу",
        "confirmNow": "Провести зараз",
        "detailsFailed": "Не вдалося завантажити деталі плану",
        "paymentRecorded": "Переказ виконано",
        "paymentRecordedDetails": "Гроші одразу зараховані в цю ціль, а прогрес оновлено.",
        "paymentTaskCreated": "Створено задачу на платіж"
      }
    }
  }
};

export default resources;
